import {
	linkWorkOrderLOTO,
	unLinkWorkOrderLOTO,
} from "@/pages/work-orders/repositorie/WorkOrdersRepositorie";
import { useWorkOrderModalsContext } from "@/shared/work-orders-modals/context/work-order-modals.context";
import type { GridApi } from "ag-grid-community";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const useWorkOrderLOTOGridActionsAndButtons = ({
	gridRef,
	searchWorkOrderLOTO,
	setShowLinkResourcesToAssetDialog,
	setRemoveLOTOError,
	setListMessage,
	WorkOrderId,
}: {
	WorkOrderId: number | null;
	newMode?: boolean;
	gridRef: GridApi | null;
	searchWorkOrderLOTO: Function;
	setRemoveLOTOError: (err: string) => void | null;
	setShowLinkResourcesToAssetDialog?: Function;
	setListMessage: (err: string) => void | null;
}) => {
	const {
		state: {
			rowSelectedInLOTO,
			workOrderLOTOList,
			LOTONumberToLink,
			LOTONameToLink,
			isCreatingLOTO,
		},
		actions: {
			setFirstTime,
			setIsCreatingLOTO,
			setWorkOrderLOTOList,
			setDataLOTOSelected,
			setWorkOrderLOTOToAdd,
			setLOTONumberToLink,
			setLOTONameToLink,
			setRowSelectedInLOTO,
			setListLOTOSelected,
		},
	} = useWorkOrderModalsContext();

	const [refreshTrigger, setRefreshTrigger] = useState(1);

	// BUTTONS STATES
	const [addButton, setAddButton] = useState<boolean>(true);
	const [deleteButton, setDeleteButton] = useState<boolean>(false);
	const [saveButton, setSaveButton] = useState<boolean>(false);
	const [discardButton, setDiscardButton] = useState<boolean>(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const isMountRef = useRef(true);
	const [searchParams] = useSearchParams();

	const refresh = useCallback(() => {
		if (!isMountRef.current) {
			const updatedList = workOrderLOTOList.filter(
				(item: any) => item.id !== -1,
			);

			setWorkOrderLOTOList(updatedList);
			setFirstTime(true);
			setSaveButton(false);
			setDiscardButton(false);
			setAddButton(true);
			setDeleteButton(false);
			setDeleteOpen(false);
			setDataLOTOSelected(undefined);
			searchWorkOrderLOTO();
		}
	}, [searchParams, WorkOrderId]);

	const saveRow = useCallback(async () => {
		if (LOTONumberToLink === "" || LOTONameToLink === "") {
			setListMessage("LOTO number and LOTO name are required");
		} else {
			const newWorkOrderLOTO = {
				LOTONumber: LOTONumberToLink,
				LOTOName: LOTONameToLink,
				IsAssetResource: false,
			};
			setWorkOrderLOTOToAdd(newWorkOrderLOTO);
			const res = await linkWorkOrderLOTO(WorkOrderId as number, [
				newWorkOrderLOTO,
			]);
			if (res.ok) {
				setIsCreatingLOTO(false);

				if (setShowLinkResourcesToAssetDialog)
					setShowLinkResourcesToAssetDialog(true);
				setLOTONumberToLink("");
				setLOTONameToLink("");
				setRefreshTrigger((prev) => prev + 1);
			} else setListMessage(res.message);
		}
	}, [
		LOTONumberToLink,
		LOTONameToLink,
		WorkOrderId,
		linkWorkOrderLOTO,
		setRemoveLOTOError,
		setRefreshTrigger,
	]);

	const addRow = useCallback(() => {
		setIsCreatingLOTO(true);
		setAddButton(false);
		setSaveButton(true);
		setDiscardButton(true);
		setDeleteButton(false);
		const newData = { id: -1, WorkOrderId: WorkOrderId };
		const currentData = workOrderLOTOList ? workOrderLOTOList : [];
		const data = [newData, ...currentData];
		setWorkOrderLOTOList(data);
	}, [workOrderLOTOList, WorkOrderId]);

	const cancelAddLOTO = useCallback(() => {
		const updatedList = workOrderLOTOList.filter((item: any) => item.id !== -1);

		setIsCreatingLOTO(false);
		setAddButton(true);
		setSaveButton(false);
		setDiscardButton(false);
		setDeleteButton(false);
		setDataLOTOSelected(null);
		setLOTONumberToLink("");
		setLOTONameToLink("");
		setWorkOrderLOTOList(updatedList);
	}, [
		workOrderLOTOList,
		setWorkOrderLOTOList,
		setDataLOTOSelected,
		setIsCreatingLOTO,
	]);

	const removeLOTO = useCallback(
		async (LOTOToRemove?: any[]) => {
			if (LOTOToRemove && LOTOToRemove.length > 0) {
				const formattedLOTO = LOTOToRemove.map((loto: any) => ({
					LOTONumber: loto.data.LOTONumber,
					LOTOName: loto.data.LOTOName,
					IsAssetResource: loto.data.IsAssetResource,
				}));

				const res = await unLinkWorkOrderLOTO(
					WorkOrderId as number,
					formattedLOTO,
				);
				if (res.ok) {
					refresh();
				} else {
					setRemoveLOTOError(res.message);
				}
			} else if (rowSelectedInLOTO) {
				const formattedLOTO = [
					{
						LOTONumber: rowSelectedInLOTO.LOTONumber,
						LOTOName: rowSelectedInLOTO.LOTOName,
						IsAssetResource: rowSelectedInLOTO.IsAssetResource,
					},
				];

				const res = await unLinkWorkOrderLOTO(
					WorkOrderId as number,
					formattedLOTO,
				);
				if (res.ok) {
					refresh();
				} else {
					setRemoveLOTOError(res.message);
				}
			}

			setRowSelectedInLOTO(null);
			setDataLOTOSelected(null);
			setDeleteOpen(false);
			setAddButton(true);
			setSaveButton(false);
			setDiscardButton(false);
			setDeleteButton(false);
			setListLOTOSelected([]);

			if (gridRef) {
				gridRef.deselectAll();
			}
		},
		[
			WorkOrderId,
			unLinkWorkOrderLOTO,
			refresh,
			setRemoveLOTOError,
			rowSelectedInLOTO,
			setRowSelectedInLOTO,
			setDataLOTOSelected,
			setDeleteOpen,
			setAddButton,
			setSaveButton,
			setDiscardButton,
			setDeleteButton,
			setListLOTOSelected,
			gridRef,
		],
	);

	useEffect(() => {
		refresh();
	}, [refreshTrigger]);

	useEffect(() => {
		isMountRef.current = false;
	}, []);

	return {
		refresh,
		saveRow,
		addRow,
		removeLOTO,
		cancelAddLOTO,
		addButton,
		deleteButton,
		saveButton,
		discardButton,
		deleteOpen,
		setSaveButton,
		setDiscardButton,
		setAddButton,
		setDeleteButton,
		setDeleteOpen,
	};
};
